import React from 'react';
import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { DrawerContainer } from '../../../components/Drawer/DrawerContainer';
import { DrawerHeaderWithLeftIcon } from '../../../components/Drawer/DrawerHeader';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';

interface EstablecimientoSeleccionadoResumen {
    id: number;
    codigoSRT: string;
    nombre: string;
    contrato: string;
}

interface Props {
    open: boolean;
    setOpen: (value: boolean) => void;
    establishments: EstablecimientoSeleccionadoResumen[];
    onRemove: (id: number) => void;
}

const EstablecimientosSeleccionadosDrawer: React.FC<Props> = ({
    open,
    setOpen,
    establishments,
    onRemove
}) => {
    const { t } = useTranslation('visitas');
    const dispatch = useAppDispatch();
    const closeDrawer = () => setOpen(false);

    const handleRemoveClick = (establecimiento: EstablecimientoSeleccionadoResumen) => {
        dispatch(
            showAlert({
                title: t('alerts.removeSelectedEstablishment.title', {
                    nombre: establecimiento.nombre
                }),
                confirmAction: () => onRemove(establecimiento.id),
                confirmText: t('alerts.removeSelectedEstablishment.confirmText'),
                cancelText: t('alerts.removeSelectedEstablishment.cancelText'),
                icon: 'info'
            })
        );
    };

    return (
        <DrawerContainer open={open} setOpen={setOpen} customWidth="65vw">
            <DrawerHeaderWithLeftIcon
                onIconClick={closeDrawer}
                text={t('newVisit.selectedEstablishmentsCount', { count: establishments.length })}
                LeftIcon={<AssignmentOutlinedIcon color="success" />}
            />
            <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
                <Table
                    stickyHeader
                    size="small"
                    sx={{
                        '& tbody tr:nth-of-type(odd)': {
                            backgroundColor: 'rgba(0, 0, 0, 0.03)'
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('table.columnsTitles.srtCode')}</TableCell>
                            <TableCell>{t('table.columnsTitles.name')}</TableCell>
                            <TableCell>{t('table.columnsTitles.contract')}</TableCell>
                            <TableCell padding="checkbox" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {establishments.length > 0 ? (
                            establishments.map((est) => (
                                <TableRow key={est.id}>
                                    <TableCell>{est.codigoSRT}</TableCell>
                                    <TableCell>{est.nombre}</TableCell>
                                    <TableCell>{est.contrato}</TableCell>
                                    <TableCell padding="checkbox">
                                        <Tooltip title={t('newVisit.removeSelectedEstablishmentTooltip')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveClick(est)}
                                                aria-label={t(
                                                    'newVisit.removeSelectedEstablishmentTooltip'
                                                ) as string}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography variant="body2" align="center" sx={{ py: 2 }}>
                                        {t('table.noData.noEstablishmentsWithFilters')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </DrawerContainer>
    );
};

export default EstablecimientosSeleccionadosDrawer;
